import { Divider, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import RichText from "components/basic/richText";
import Button from "components/basic/button";
import TextField from "components/basic/textField";
import Typography from "components/basic/typography";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useUrlParamState from "hooks/useUrlParamState";
import SignUpCardLayout from "../../components/SignUpCardLayout";
import { useSnack } from "hooks/useSnack";
import { useFieldErrors } from "hooks/useFieldErrors";
import { Account } from "../..";
import { validateEmailForm } from "./validation";

interface EmailFormProps {
  signUpData: Account;
  setSignUpData: React.Dispatch<React.SetStateAction<Account>>;
}

const EmailForm: React.FC<EmailFormProps> = ({ signUpData, setSignUpData }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const showSnack = useSnack();
  const { t } = useTranslation("auth");
  const [returnTo] = useUrlParamState("returnTo", "nameForm");

  const [fields, setFields] = useState({
    confirmPassword: "",
  });

  const { errors, validateAll, revalidate } = useFieldErrors(validateEmailForm);

  // The form spans the shared account state and the local confirm-password box.
  const formValues = {
    email: signUpData.email,
    password: signUpData.password,
    confirmPassword: fields.confirmPassword,
  };

  // Applies an edit and re-checks any error already on screen.
  const handleChange = (name: string, value: string) => {
    if (name === "confirmPassword") {
      setFields((prev) => ({ ...prev, confirmPassword: value }));
    } else if (name === "email") {
      setSignUpData((prev) => ({
        ...prev,
        email: value,
        emailVerified: false,
      }));
    } else {
      setSignUpData((prev) => ({ ...prev, [name]: value }));
    }

    revalidate({ ...formValues, [name]: value });
  };

  const handleNextClick = () => {
    if (!validateAll(formValues)) {
      showSnack(t("common.fix_errors"), "error");
      return;
    }

    navigate(`?${new URLSearchParams({ currentPage: returnTo })}`);
  };

  return (
    <SignUpCardLayout>
      <div
        id="right section"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(2),
        }}
      >
        <Typography
          variant="h4"
          align="center"
          style={{ margin: `${theme.spacing(4)} 0` }}
        >
          {t("common.create_your_account")}
        </Typography>

        <TextField
          size="tiny"
          name="email"
          type="email"
          autoComplete="email"
          textAlign="center"
          value={signUpData.email}
          placeHolder={t("email.placeholder")}
          onChange={({ target: { name, value } }) => handleChange(name, value)}
          error={errors.email ?? undefined}
        />

        <TextField
          size="tiny"
          name="password"
          type="password"
          autoComplete="new-password"
          value={signUpData.password}
          textAlign="center"
          placeHolder={t("email.password")}
          onChange={({ target: { name, value } }) => handleChange(name, value)}
          error={errors.password ?? undefined}
        />

        <TextField
          size="tiny"
          name="confirmPassword"
          type="password"
          autoComplete="new-password"
          value={fields.confirmPassword}
          textAlign="center"
          placeHolder={t("email.confirm")}
          onChange={({ target: { name, value } }) => handleChange(name, value)}
          error={errors.confirmPassword ?? undefined}
        />
      </div>

      <div>
        <div style={{ margin: `${theme.spacing(2)} 0` }}>
          <Button
            onClick={handleNextClick}
            variant="contained"
            color="warning"
            size="small"
            style={{ color: theme.palette.common.white }}
            fullWidth
          >
            {t("common.next")}
          </Button>
        </div>

        <Button
          onClick={() => navigate("?currentPage=signUpMethod")}
          variant="contained"
          size="small"
          style={{ color: theme.palette.common.white }}
          fullWidth
        >
          {t("common.back")}
        </Button>

        <Divider
          sx={{
            borderColor: theme.palette.common.white,
            margin: `${theme.spacing(3)} 0`,
          }}
        />

        <Button
          onClick={() => navigate("/signin")}
          variant="text"
          fullWidth
          size="small"
          style={{ color: theme.palette.common.white }}
        >
          <RichText
            t={t}
            i18nKey="common.have_account"
            components={{
              a: (
                <span
                  style={{ color: "orange", marginLeft: theme.spacing(1) }}
                />
              ),
            }}
          />
        </Button>
      </div>
    </SignUpCardLayout>
  );
};

export default EmailForm;
